/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, breakpoints, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { EOI_ACTIONS, useScrollToEoiForm } from 'bundles/degree-description/hooks/useScrollToEoiForm';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';

import _t from 'i18n!nls/degree-description';

type Props = {
  eoiContainerText?: EoiTextType;
  trackingIndex?: number;
};

// The EOI box lives in the hero (ExperimentalEoiButtonsBlock); these buttons just scroll the user
// there instead of rendering a form of their own.
const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);
    margin-bottom: 0;
    ${breakpoints.up('md')} {
      background: var(--cds-color-neutral-background-primary);
      border-radius: 8px;
      margin-bottom: var(--cds-spacing-200);
      padding: var(--cds-spacing-200);
    }

    .rc-Markdown.styled {
      h2 {
        ${typography2.titleSmall};
        color: var(--cds-color-neutral-primary);
        margin: 0;
      }

      p {
        ${typography2.bodySecondary};
        color: var(--cds-color-neutral-primary-weak);
        margin: 0;
      }
    }
  `,
  buttonsArea: css`
    display: grid;
    grid-template-columns: 1fr;
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);
  `,
};

const ExperimentalEoiAtAGlanceBlock = ({ eoiContainerText, trackingIndex }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('eoi', trackingIndex);
  const scrollToForm = useScrollToEoiForm(trackingIndex);

  return (
    <div css={styles.container} data-testid="experimental-eoi-at-a-glance">
      <div ref={pageSectionRef}>
        {eoiContainerText?.programInterestText && (
          <div className="eoi-header-text">
            <Markdown source={eoiContainerText.programInterestText} />
          </div>
        )}
        <div css={styles.buttonsArea}>
          <Button variant="primary" fullWidth onClick={() => scrollToForm(EOI_ACTIONS.requestInfo)}>
            {_t('Request info')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ExperimentalEoiAtAGlanceBlock);
